'use client';

import { useId, useMemo } from 'react';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { token } from '@/lib/theme/tokens';
import { useThemeVersion } from '@/lib/theme/useThemeColors';
import type { GoalSummary } from '@/types';
import {
  formatYmShort,
  goalStatus,
  monthsUntilDeadline,
  projectedMonthsToTarget,
  runtimeStatusColor,
  runtimeStatusLabel,
} from './helpers';

function monthLabel(monthsFromToday: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + monthsFromToday);
  return formatYmShort(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
}

interface Props {
  goals: GoalSummary[];
  onPickGoal: (g: GoalSummary) => void;
}

const MAX_MONTHS = 60;

type Row = {
  id: string;
  name: string;
  monthsNeeded: number;
  monthsDeadline: number | null;
  color: string;
  status: ReturnType<typeof goalStatus>;
};

// Colours are resolved at render via `runtimeStatusColor` so the legend always
// matches the (token-backed) bar colours.
const LEGEND: ReadonlyArray<{
  status: ReturnType<typeof goalStatus>;
  label: string;
}> = [
  { status: 'ok', label: 'no ritmo' },
  { status: 'tight', label: 'apertado' },
  { status: 'behind', label: 'atrasada' },
  { status: 'untracked', label: 'sem dados' },
];

function TimelineChart({
  rows,
  xMax,
  xTicks,
  height,
  onPickGoal,
}: {
  rows: Row[];
  xMax: number;
  xTicks: number[];
  height: number;
  onPickGoal: (rowId: string) => void;
}) {
  const themeVersion = useThemeVersion();
  const option = useMemo<EChartsOption>(() => {
    const colorRisk = token('--error', '#ef4444');
    const colorNeutral = token('--text-secondary', '#64748b');
    const colorAxis = token('--border', '#e2e8f0');
    const colorAxisText = token('--text-primary', '#0f172a');
    const tickSet = new Set(xTicks);
    const scatterData = rows
      .filter((r) => r.monthsDeadline !== null)
      .map((r) => ({
        value: [r.monthsDeadline as number, r.name],
        itemStyle: {
          color: '#ffffff',
          borderColor: r.monthsNeeded > (r.monthsDeadline as number) ? colorRisk : colorNeutral,
          borderWidth: 2,
        },
      }));
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (raw: unknown) => {
          const arr = Array.isArray(raw) ? raw : [raw];
          const first = arr[0] as { dataIndex?: number };
          const idx = first.dataIndex ?? -1;
          const r = rows[idx];
          if (!r) return '';
          const need = r.monthsNeeded >= MAX_MONTHS
            ? `${MAX_MONTHS}+ meses`
            : `${r.monthsNeeded} ${r.monthsNeeded === 1 ? 'mês' : 'meses'}`;
          const deadline = r.monthsDeadline !== null
            ? `<div style="color:#cbd5e1;">Prazo: <span style="color:#f8fafc;font-family:monospace;">${r.monthsDeadline} ${r.monthsDeadline === 1 ? 'mês' : 'meses'}</span></div>`
            : '';
          return `<div style="min-width:160px;"><div style="font-size:11px;font-weight:600;color:#f8fafc;margin-bottom:3px;">${r.name}</div><div style="font-size:11px;color:#cbd5e1;">No ritmo atual: <span style="color:#f8fafc;font-family:monospace;">${need}</span></div>${deadline}<div style="margin-top:3px;font-size:11px;font-weight:500;color:${r.color};">${runtimeStatusLabel(r.status)}</div></div>`;
        },
      },
      grid: { left: 8, right: 28, top: 4, bottom: 24, containLabel: true },
      xAxis: {
        type: 'value',
        min: 0,
        max: xMax,
        axisLine: { lineStyle: { color: colorAxis } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          fontFamily: 'ui-monospace, monospace',
          // Month ticks are wide monospace labels on a value axis; at narrow
          // widths they collide into an unreadable blob. Shed the colliding ones.
          hideOverlap: true,
          interval: (_idx: number, val: string) => tickSet.has(Number(val)),
          formatter: (v: number) => (v === 0 ? 'hoje' : monthLabel(v)),
        },
      },
      yAxis: {
        type: 'category',
        data: rows.map((r) => r.name),
        inverse: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: colorAxisText, fontFamily: 'ui-monospace, monospace' },
      },
      series: [
        {
          name: 'deadline',
          type: 'bar',
          barWidth: 6,
          barGap: '-100%',
          itemStyle: { color: colorAxis, borderRadius: 6 },
          data: rows.map((r) => r.monthsDeadline ?? 0),
          silent: true,
          animation: false,
        },
        {
          name: 'needed',
          type: 'bar',
          barWidth: 14,
          cursor: 'pointer',
          itemStyle: { borderRadius: 6 },
          data: rows.map((r) => ({ value: r.monthsNeeded, itemStyle: { color: r.color, borderRadius: 6 } })),
        },
        {
          type: 'scatter',
          symbolSize: 10,
          data: scatterData,
          silent: true,
          z: 5,
        },
      ],
    };
    // themeVersion: option colours are read from CSS tokens — rebuild on a theme flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, xMax, xTicks, themeVersion]);

  const handleClick = (params: unknown) => {
    const p = params as { seriesName?: string; dataIndex?: number };
    if (p.seriesName !== 'needed') return;
    const row = rows[p.dataIndex ?? -1];
    if (row) onPickGoal(row.id);
  };

  return (
    <div style={{ width: '100%', height }}>
      <EChart option={option} onEvents={{ click: handleClick }} />
    </div>
  );
}

export function PortfolioTimeline({ goals, onPickGoal }: Props) {
  const titleId = useId();
  const summaryId = useId();
  const themeVersion = useThemeVersion();

  const rows = useMemo<Row[]>(() => {
    return goals.map((g) => {
      const raw = projectedMonthsToTarget(g);
      const monthsNeeded = raw === Infinity ? MAX_MONTHS : Math.min(raw, MAX_MONTHS);
      const monthsDeadline = g.targetDate ? monthsUntilDeadline(g.targetDate) : null;
      const status = goalStatus(g);
      return {
        id: g.id,
        name: g.name,
        monthsNeeded,
        monthsDeadline:
          monthsDeadline !== null && monthsDeadline >= 0
            ? Math.min(monthsDeadline, MAX_MONTHS)
            : null,
        color: runtimeStatusColor(status),
        status,
      };
    });
    // themeVersion: runtimeStatusColor reads CSS tokens at runtime — recompute on a theme flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, themeVersion]);

  const goalsById = useMemo(() => new Map(goals.map((g) => [g.id, g])), [goals]);

  const xMax = useMemo(() => {
    const values = rows.flatMap((r) => [r.monthsNeeded, r.monthsDeadline ?? 0]);
    const max = Math.max(12, ...values);
    return Math.ceil(max / 6) * 6;
  }, [rows]);

  const xTicks = useMemo(() => {
    const step = xMax > 24 ? 6 : xMax > 12 ? 3 : 1;
    const ticks: number[] = [];
    for (let v = 0; v <= xMax; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] !== xMax) ticks.push(xMax);
    return ticks;
  }, [xMax]);

  const height = Math.max(140, rows.length * 40 + 56);

  const summary = useMemo(() => {
    if (rows.length === 0) return 'Nenhuma meta cadastrada.';
    const sentences = rows.map((r) => {
      const need =
        r.monthsNeeded >= MAX_MONTHS
          ? `mais de ${MAX_MONTHS} meses`
          : `${r.monthsNeeded} meses no ritmo atual`;
      const deadline =
        r.monthsDeadline !== null
          ? `, prazo de ${r.monthsDeadline} ${r.monthsDeadline === 1 ? 'mês' : 'meses'}`
          : ', sem prazo';
      return `${r.name}: ${need}${deadline} — ${runtimeStatusLabel(r.status)}.`;
    });
    return `${rows.length} ${rows.length === 1 ? 'meta' : 'metas'} no portfólio. ${sentences.join(' ')}`;
  }, [rows]);

  return (
    <section
      aria-labelledby={titleId}
      aria-describedby={summaryId}
      className="h-full rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)] p-4"
    >
      <p id={summaryId} className="sr-only">
        {summary}
      </p>

      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id={titleId} className="text-sm font-semibold text-text-primary">
            Linha do tempo
          </h3>
          <p className="mt-0.5 text-xs text-text-tertiary">
            Meses até concluir vs. prazo
          </p>
        </div>
        <ul role="list" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-text-secondary">
          {LEGEND.map((l) => (
            <li key={l.status} className="flex items-center gap-1.5">
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: runtimeStatusColor(l.status) }}
                aria-hidden="true"
              />
              <span>{l.label}</span>
            </li>
          ))}
        </ul>
      </header>

      {rows.length === 0 ? (
        <p className="py-10 text-center text-xs text-text-tertiary">Nenhuma meta para exibir.</p>
      ) : (
        <>
          <TimelineChart
            rows={rows}
            xMax={xMax}
            xTicks={xTicks}
            height={height}
            onPickGoal={(rowId) => {
              const g = goalsById.get(rowId);
              if (g) onPickGoal(g);
            }}
          />
          <ul
            role="list"
            className="mt-2 flex items-center justify-end gap-3 text-2xs text-text-tertiary"
          >
            <li className="flex items-center gap-1">
              <span
                className="size-2 rounded-full border-2 border-border-strong bg-surface"
                aria-hidden="true"
              />
              <span>prazo declarado</span>
            </li>
            <li className="flex items-center gap-1">
              <span
                className="size-2 rounded-full border-2 border-error bg-surface"
                aria-hidden="true"
              />
              <span>prazo em risco</span>
            </li>
          </ul>
        </>
      )}
    </section>
  );
}
